export const translations = {
  en: {
    // Navigation
    home: 'Home',
    features: 'Features',
    pricing: 'Pricing',
    about: 'About',
    contact: 'Contact',
    login: 'Log in',
    signup: 'Sign up',
    language: 'Language',

    // Home Page
    heroTitle: 'Verify Your Clients, Stay Compliant.',
    heroSubtitle: 'Screen individuals and entities against global sanctions lists with advanced AI matching. Transform manual compliance processes into automated workflows with audit-ready documentation.',
    getStarted: 'Get Started',
    learnMore: 'Learn More',
    trustedBy: 'Trusted by leading companies worldwide',
    globalWatchlists: 'Global Watchlists',
    superfastResults: 'Superfast Results',
    auditReady: 'Audit Ready',
    gdprCompliant: 'GDPR Compliant',
    
    // Challenges Section
    challengesTitle: 'Stop Wrestling with Manual Compliance Screening',
    challengesSubtitle: 'Traditional compliance screening is slow, error-prone, and resource-intensive. Your business needs a smarter approach.',
    beforeProtego: 'Before Sanction Screener',
    beforeProtegoPoints: [
      'Manual screening takes hours or days',
      'High false positive rates waste time',
      'Multiple systems create fragmentation',
      'Inconsistent documentation for audits',
      'Risk of human error in critical decisions',
      'Delayed customer onboarding'
    ],
    afterProtego: 'After Sanction Screener',
    afterProtegoPoints: [
      'Automated screening in sub-seconds',
      'Smart AI reduces false positives by 80%',
      'Single platform for all global watchlists',
      'Automatic audit-ready documentation',
      'Consistent, reliable automated decisions',
      'Accelerated compliant onboarding'
    ],
    
    // Pillars Section
    pillarsTitle: 'Three Pillars of Compliance Excellence',
    pillarsSubtitle: 'Sanction Screener transforms compliance from a burden into a competitive advantage',
    pillars: [
      {
        icon: '⚡',
        title: 'Efficiency Through Automation',
        description: 'Transform days of manual work into seconds of automated processing. Free your compliance team to focus on strategic initiatives.'
      },
      {
        icon: '🌐',
        title: 'Comprehensive Coverage',
        description: 'Access all major global watchlists through a single platform. EU, UN, OFAC, PEP registers, and more – all in one place.'
      },
      {
        icon: '📋',
        title: 'Audit-Ready Documentation',
        description: 'Generate verifiable reports with unique verification IDs. Demonstrate compliance to regulators with confidence.'
      }
    ],
    
    // Features
    featuresTitle: 'Powerful Features for Modern Compliance',
    featuresSubtitle: 'Everything you need for streamlined compliance screening and risk management',
    
    // Features Showcase Section
    productSuite: 'The Sanctions Screener Suite',
    featuresShowcaseTitle: 'The Industry\'s Most Comprehensive Sanctions Screening Platform',
    featuresShowcaseSubtitle: 'Transform compliance screening from a manual burden into a competitive advantage with our integrated platform that covers every aspect of sanctions compliance.',
    individualSearch: 'Individual & Company Search',
    caseManagement: 'Case Management',
    caseManagementDesc: 'Efficiently manage and track potential matches through a structured workflow. Our case management system provides complete visibility into review processes with comprehensive audit trails for regulatory compliance.',
    automatedMonitoring: 'Automated Monitoring',
    massUpload: 'Bulk Upload',
    individualSearchTitle: 'Lightning-Fast Individual & Company Search',
    individualSearchDesc: 'Screen individuals and companies against global sanctions lists in seconds. Our intelligent algorithms deliver accurate results with confidence scoring, reducing false positives while ensuring no genuine risks are missed.',
    individualSearchPoints: [
      'Sub-second screening against all major global watchlists',
      'Smart fuzzy matching recognizes name variations and aliases',
      'Detailed confidence scoring (0-100%) for prioritized review',
      'Support for multiple languages and non-Latin scripts',
      'Instant audit-ready reports with unique verification IDs',
    ],
    caseManagementTitle: 'Streamlined Case Management',
    caseManagementDesc: 'Efficiently manage and track potential matches through a structured workflow. Our case management system provides complete visibility into review processes with comprehensive audit trails for regulatory compliance.',
    caseManagementPoints: [
      'Centralized dashboard for all case statuses and progress',
      'Structured workflow with customizable review stages',
      'Complete audit trails with timestamps and user tracking',
      'Collaborative review process with team assignments',
      'Automated notifications and deadline management',
    ],
    automatedMonitoringTitle: 'Continuous Automated Monitoring',
    automatedMonitoringDesc: 'Stay compliant with automated re-screening that monitors your entities against evolving sanctions lists. Set custom frequencies and receive instant alerts when new risks are identified, ensuring ongoing compliance without manual intervention.',
    automatedMonitoringPoints: [
      'Configurable screening frequencies (daily to annually)',
      'Automatic alerts when watchlists update',
      'Instant notifications for newly identified risks',
      'Bulk monitoring management capabilities',
      'Historical tracking of all monitoring activities',
    ],
    massUploadTitle: 'High-Volume Bulk Upload',
    massUploadDesc: 'Process hundreds of entities simultaneously with our bulk screening capabilities. Perfect for onboarding new databases, periodic compliance reviews, or comprehensive supply chain verification with detailed batch processing and results management.',
    massUploadPoints: [
      'Upload up to 250 entities per batch via CSV/Excel',
      'Parallel processing for maximum speed and efficiency',
      'Detailed batch results with individual match reports',
      'Progress tracking and job management dashboard',
      'Exportable results for further analysis and documentation',
    ],
    trySearchDemo: 'Try Search Demo',
    sanctionsScreening: 'Sanctions Screening',
    welcomeUser: 'Welcome, User',
    searchPlaceholder: 'Enter a name to check against sanctions lists...',
    people: '👤 People',
    companies: '🏢 Companies',
    search: 'Search',
    advancedSearch: 'Advanced Search',
    advancedSearchDesc: 'Intelligent name-matching algorithms recognize variations in spelling and structure.',
    confidenceScoringTitle: 'Confidence Scoring',
    confidenceScoringDesc: 'Results ranked with detailed confidence levels to identify relevant matches.',
    complianceReady: 'Compliance Ready',
    complianceReadyDesc: 'Generate screening reports with unique IDs for regulatory compliance.',
    smartMatching: 'Smart Matching Algorithm',
    smartMatchingDesc: 'Advanced fuzzy matching algorithms recognize name variations, misspellings, and transliterations across multiple languages. Machine learning components improve accuracy over time, reducing false positives by up to 80% with detailed confidence scoring (0-100%).',
    riskClassification: 'Risk Classification System',
    riskClassificationDesc: 'Clear visual indicators with color-coded risk levels (Very High, High, Medium, Low). Customizable risk thresholds match your organization\'s appetite while intelligent prioritization highlights matches above 95% confidence for immediate attention.',
    continuousMonitoring: 'Continuous Monitoring',
    continuousMonitoringDesc: 'Automated re-screening when watchlists update, with configurable periods (daily, weekly, monthly, yearly). Immediate alerts for newly identified risks and change detection highlight specific modifications in risk status.',
    massScreening: 'Bulk Screening Capabilities',
    massScreeningDesc: 'Bulk upload and process thousands of entities simultaneously with CSV/Excel compatibility. Structured batch processing with detailed results and screening job management for both small and large datasets.',
    apiIntegration: 'Seamless API Integration',
    apiIntegrationDesc: 'RESTful API with comprehensive documentation, pre-built connectors for popular CRM and ERP systems. Webhook support for event-driven architectures and customizable data mapping for complex integrations.',

    // How It Works Section
    howItWorksTitle: 'How Sanction Screener Works',
    howItWorksSubtitle: 'A simple yet powerful workflow to ensure your business remains compliant',
    connect: 'Connect',
    connectDesc: 'Your compliance solution with enterprise-grade security and a user-friendly web interface. No technical expertise required.',
    screen: 'Screen',
    screenDesc: 'Instantly check individuals and entities against major global watchlists including EU, UN, and OFAC with a response in seconds.',
    review: 'Review',
    reviewDesc: 'Examine potential matches through an intelligent case management system that highlights key risk factors and reduces false positives.',
    decide: 'Decide',
    decideDesc: 'Approve, reject, or escalate cases with confidence, maintaining comprehensive audit trails that satisfy regulatory requirements.',
    beyondBasic: 'Beyond basic screening',
    beyondBasicDesc: 'Sanction Screener doesn\'t just stop at screening. We provide continuous monitoring, automated re-screening when sanctions lists change, and detailed analytics to help you optimize your compliance processes. With our enterprise plan, you\'ll also receive regulatory updates and compliance advisory services to stay ahead of changing requirements.',

    // Pricing
    pricingTitle: 'Pricing Plans',
    pricingSubtitle: 'Choose the right plan for your compliance needs. Upgrade anytime as your organization grows.',
    starter: 'Starter',
    business: 'Business',
    enterprise: 'Enterprise',
    mostPopular: 'Most popular',
    custom: 'Custom',
    perMonth: '/month',
    unlimitedScreenings: 'Unlimited screenings',
    unlimitedTeam: 'Unlimited team members',
    premiumApi: 'Premium API access',
    oneYearHistory: '1-year search history',
    dedicatedManager: 'Dedicated account manager',
    upTo1000Screenings: 'Up to 100 screenings/month',
    upTo10000Screenings: 'Up to 1000 screenings/month',
    threeTeamMembers: '3 team members',
    tenTeamMembers: '10 team members',
    basicApiAccess: 'Basic API access',
    fullApiAccess: 'Full API access',
    sevenDayHistory: '7-day search history',
    thirtyDayHistory: '30-day search history',
    batchScreening: 'Batch screening',

    // Pricing Page
    pricingPage: {
      heroTitle: 'Transparent Pricing',
      heroSubtitle: 'Choose the perfect plan for your compliance needs with no hidden fees.',
      annualBilling: 'Annual Billing',
      monthlyBilling: 'Monthly Billing',
      save20: 'Save 20%',
      youSavePerYear: 'You save {amount} per year',
      starterPlan: {
        title: 'Starter',
        subtitle: 'Perfect for small businesses and startups',
        priceMonthly: '€99',
        priceAnnual: '€79',
        billedAnnually: 'Billed annually (€948/year)',
        features: [
          'Up to 100 searches per month',
          '3 team members',
          'Active Monitoring',
          'Case Management'
        ]
      },
      professionalPlan: {
        title: 'Professional',
        subtitle: 'Ideal for growing companies with compliance needs',
        priceMonthly: '€199',
        priceAnnual: '€159',
        billedAnnually: 'Billed annually (€1,908/year)',
        features: [
          'Up to 1000 searches per month',
          '10 team members',
          'Batch screening',
          'REST API Access',
          'Active Monitoring',
          'Case Management'
        ]
      },
      enterprisePlan: {
        title: 'Enterprise',
        subtitle: 'For large organizations with complex compliance requirements',
        price: 'Custom',
        tailoredToNeeds: 'Tailored to your needs',
        features: [
          'Unlimited searches',
          'Dedicated account manager',
          'Custom integrations',
          'Premium support',
          'SLA guarantee',
          'Advanced security & compliance'
        ]
      },
      compareFeatures: {
        title: 'Compare Features',
        subtitle: 'Find the plan that\'s right for your compliance needs',
        features: 'Features',
        monthlyScreenings: 'Monthly screenings',
        euSanctionsLists: 'EU sanctions lists',
        unSanctionsLists: 'UN sanctions lists',
        ofacSanctionsLists: 'OFAC sanctions lists',
        nationalSanctionsLists: 'National sanctions lists',
        pepsScreening: 'PEPs screening',
        adverseMediaScreening: 'Adverse media screening',
        fuzzyMatchingAlgorithm: 'Fuzzy matching algorithm',
        apiAccess: 'API access',
        webhookNotifications: 'Webhook notifications',
        auditLogs: 'Audit logs',
        customRiskRules: 'Custom risk rules',
        onPremiseDeployment: 'On-premise deployment',
        support: 'Support',
        limited: 'Limited',
        basic: 'Basic',
        advanced: 'Advanced',
        premium: 'Premium',
        email: 'Email',
        emailAndChat: 'Email & Chat',
        priority247: '24/7 Priority',
        sevenDays: '7 days',
        thirtyDays: '30 days',
        oneYear: '1 year',
        aiPoweredMatching: 'AI-powered matching',
        bulkUpload: 'Bulk upload',
        caseManagement: 'Case management',
        activeMonitoring: 'Active monitoring',
        // Add new keys for the actual values to be used in the table
        starterMonthlyScreenings: '100',
        professionalMonthlyScreenings: '1000',
        enterpriseMonthlyScreenings: 'Unlimited screenings'
      },
      faq: {
        title: 'Frequently Asked Questions',
        subtitle: 'Have questions? We\'re here to help.',
        stillHaveQuestions: 'Still have questions?',
        trialEnd: {
          question: 'What happens after my free trial?',
          answer: 'After your 14-day free trial ends, your account will automatically switch to the plan you selected during sign-up. We\'ll send you reminders before the trial ends, and you can cancel or change your plan at any time with no penalties.'
        },
        paymentMethods: {
          question: 'What payment methods do you accept?',
          answer: 'We accept all major credit cards, including Visa, Mastercard, and American Express. For Enterprise plans, we also offer invoice payments with net-30 terms.'
        },
        changePlans: {
          question: 'Can I change plans later?',
          answer: 'Yes, you can upgrade or downgrade your plan at any time. Upgrades take effect immediately, while downgrades will take effect at the end of your current billing cycle.'
        },
        exceedLimit: {
          question: 'What happens if I exceed my monthly screening limit?',
          answer: 'If you approach your screening limit, we\'ll notify you so you can upgrade if needed. If you exceed your limit, additional screenings will be charged at a per-screening rate based on your plan.'
        },
        nonProfit: {
          question: 'Do you offer discounts for non-profit organizations?',
          answer: 'Yes, we offer special pricing for qualified non-profit organizations. Please contact our sales team to learn more about our non-profit program.'
        },
        contractPeriod: {
          question: 'Is there a minimum contract period?',
          answer: 'No, our Starter and Professional plans are month-to-month with no minimum contract period when billed monthly. Annual plans are prepaid for 12 months. Enterprise plans may have custom terms.'
        }
      },
      cta: {
        title: 'Ready to Strengthen Your Compliance?',
        subtitle: 'Join thousands of businesses across Europe who trust Sanction Screener.',
        startFreeTrial: 'Start Free Trial',
        requestDemo: 'Request Demo'
      }
    },

    // CTA Section
    startFreeTrial: 'Start Free Trial',
    contactSales: 'Contact Sales',
    
    // Newsletter Section
    newsletterTitle: 'Stay Updated',
    newsletterSubtitle: 'Subscribe to our newsletter for the latest updates on compliance, new features, and exclusive offers.',
    newsletterPlaceholder: 'Enter your email address',
    newsletterButton: 'Subscribe',
    newsletterSuccess: 'Thank you for subscribing!',
    newsletterError: 'An error occurred. Please try again later.',
    
    // About
    aboutTitle: 'About Sanction Screener',
    aboutSubtitle: 'Your trusted partner in cybersecurity',
    ourMission: 'Our Mission',
    ourMissionDesc: 'To provide cutting-edge cybersecurity solutions that protect businesses in the digital age',
    ourTeam: 'Our Team',
    ourTeamDesc: 'Experienced professionals dedicated to your security',
    
    // Contact
    contactTitle: 'Get in Touch',
    contactSubtitle: 'We\'re here to help',
    name: 'Name',
    email: 'Email',
    message: 'Message',
    send: 'Send Message',
    contactInfo: 'Contact Information',
    address: 'Address',
    phone: 'Phone',
    
    // Auth
    emailAddress: 'Email Address',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    forgotPassword: 'Forgot Password?',
    rememberMe: 'Remember Me',
    createAccount: 'Create Account',
    alreadyHaveAccount: 'Already have an account?',
    dontHaveAccount: 'Don\'t have an account?',
    
    // Footer
    companyName: 'Sanction Screener',
    companyDescription: 'Advanced sanctions screening solution for financial institutions, fintech companies, and regulated businesses across Europe.',
    quickLinks: 'Quick Links',
    aboutUs: 'About Us',
    contactUs: 'Contact Us',
    emailLabel: 'Email:',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    gdprCompliance: 'GDPR Compliance',
    allRightsReserved: 'All rights reserved',
    euCompliance: 'Compliant with EU regulations including 5AMLD and 6AMLD. Registered in the European Union.',
    
    // Common
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    view: 'View',
    filter: 'Filter',
    sort: 'Sort',
    more: 'More',
    less: 'Less',

    // Cookie Consent
    cookieConsent: {
      title: 'Cookie Preferences',
      description: 'We use cookies to enhance your experience, analyze site traffic, and provide personalized content. You can customize your preferences below.',
      acceptAll: 'Accept All',
      rejectAll: 'Reject All',
      customize: 'Customize',
      settingsTitle: 'Cookie Settings',
      saveSettings: 'Save Settings',
      cancel: 'Cancel',
      essential: {
        title: 'Essential Cookies',
        description: 'Essential cookies are required for basic site functionality.'
      },
      analytics: {
        title: 'Analytics Cookies',
        description: 'Help us understand how visitors interact with our website.'
      },
      marketing: {
        title: 'Marketing Cookies',
        description: 'Allow us to show you relevant ads and content.'
      }
    },

    // Privacy Policy
    lastUpdated: 'Last updated',
    privacy: {
      introduction: {
        title: '1. Introduction',
        content: 'Sanction Screener ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how your personal information is collected, used, and disclosed by Sanction Screener when you use our website, services, and applications (collectively, the "Services").'
      },
      informationCollection: {
        title: '2. Information We Collect',
        subtitle: 'We collect information that you provide directly to us, such as:',
        directInfo: [
          'Account information: When you register for an account, we collect your name, email address, password, company name, job title, and contact details.',
          'Sanctions screening data: Information you submit for sanctions screening, which may include individual or entity names, identifiers, and related information.',
          'Payment information: When you subscribe to our paid services, we collect payment information, though we do not store complete payment card details.',
          'Communications: When you contact us directly, we record the communications and any additional information you provide.'
        ],
        automaticSubtitle: 'We also automatically collect certain information when you use our Services:',
        automaticInfo: [
          'Usage data: Information about how you use our Services, including log data, device information, and analytics data.',
          'Cookies and similar technologies: We use cookies and similar technologies to collect information about your browsing activities.'
        ]
      },
      informationUse: {
        title: '3. How We Use Your Information',
        subtitle: 'We use the information we collect to:',
        uses: [
          'To provide, maintain, and improve our Services.',
          'To process your transactions and manage your subscriptions.',
          'To respond to your comments, questions, and requests.',
          'To send you technical notices, updates, security alerts, and support messages.',
          'To communicate with you about products, services, offers, and events offered by Sanction Screener.'
        ]
      },
      dataRetention: {
        title: '4. Data Retention',
        content: 'We retain personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. For sanctions screening data, we maintain records in accordance with applicable anti-money laundering regulations and compliance requirements.'
      },
      dataSharing: {
        title: '5. Data Sharing and Disclosure',
        subtitle: 'We may share your information in the following situations:',
        situations: [
          'With service providers and business partners who help us deliver our Services;',
          'In connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business;',
          'If we believe disclosure is necessary to comply with applicable laws, regulations, legal processes, or governmental requests;',
          'To enforce our agreements, policies, and terms of service;',
          'To protect the security or integrity of our Services;',
          'With your consent or at your direction.'
        ]
      },
      gdpr: {
        title: '6. GDPR Compliance',
        content: 'For users in the European Economic Area (EEA), we process personal data in accordance with the General Data Protection Regulation (GDPR). We act as both a data controller and processor, depending on the context.',
        rightsSubtitle: 'Under the GDPR, you have the following rights:',
        rights: [
          'Right to access your personal data;',
          'Right to rectification of inaccurate personal data;',
          'Right to erasure of your personal data;',
          'Right to restrict processing of your personal data;',
          'Right to data portability;',
          'Right to object to processing of your personal data;',
          'Right to lodge a complaint with a supervisory authority.'
        ]
      },
      dataSecurity: {
        title: '7. Data Security',
        content: 'We prioritize the security of your personal information and have implemented comprehensive technical, administrative, and physical safeguards designed to protect your data. Our security measures include robust encryption protocols, secure database configuration, regular security audits, and strict access controls. While we are committed to maintaining high security standards and continuously enhancing our protections, it\'s important to note that no system can provide a 100% guarantee against all possible security threats in today\'s digital environment.'
      },
      internationalTransfers: {
        title: '8. International Data Transfers',
        content: 'Your information may be transferred to, and processed in, countries other than the country in which you reside. These countries may have data protection laws different from those in your country. We ensure appropriate safeguards are in place to protect your personal information when transferred internationally.'
      },
      changes: {
        title: '9. Changes to This Privacy Policy',
        content: 'We may modify this Privacy Policy from time to time. If we make material changes, we will notify you by email or through the Services prior to the changes becoming effective.'
      },
      contact: {
        title: '10. Contact Us',
        content: 'If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:',
        email: 'Email'
      }
    },

    // Terms of Service
    termsOfService: {
      title: 'Terms of Service',
      acceptance: {
        title: 'Acceptance of Terms',
        content: 'By accessing and using Sanction Screener\'s services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.'
      },
      services: {
        title: 'Services Description',
        content: 'Sanction Screener provides sanctions screening and compliance verification services. We reserve the right to modify, suspend, or discontinue any aspect of our services at any time.'
      },
      userObligations: {
        title: 'User Obligations',
        items: [
          'Provide accurate and complete information when using our services',
          'Maintain the confidentiality of your account credentials',
          'Use our services in compliance with all applicable laws and regulations',
          'Not attempt to reverse engineer or interfere with our services',
          'Not use our services for any illegal or unauthorized purposes'
        ]
      },
      privacy: {
        title: 'Privacy and Data Protection',
        content: 'We are committed to protecting your privacy and handling your data in accordance with applicable data protection laws. Our Privacy Policy explains how we collect, use, and protect your information.'
      },
      liability: {
        title: 'Limitation of Liability',
        content: 'Sanction Screener shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use our services.'
      },
      termination: {
        title: 'Termination',
        content: 'We reserve the right to terminate or suspend your access to our services at any time, without notice, for any reason, including if we believe you have violated these Terms of Service.'
      },
      changes: {
        title: 'Changes to Terms',
        content: 'We may modify these Terms of Service at any time. We will notify you of any material changes by posting the new Terms of Service on our website.'
      },
      contact: {
        title: 'Contact Information',
        content: 'If you have any questions about these Terms of Service, please contact us at support@sanctionscreener.eu.'
      }
    },

    // GDPR Page
    gdpr: {
      title: 'GDPR Compliance',
      introduction: {
        title: 'Introduction',
        content: 'At Sanction Screener, we are committed to protecting your privacy and ensuring the security of your personal data. This GDPR compliance page outlines how we collect, process, and protect your personal information in accordance with the General Data Protection Regulation (GDPR) and other applicable data protection laws.'
      },
      rights: {
        title: 'Your Rights Under GDPR',
        list: [
          {
            title: 'Right to Access',
            description: 'You have the right to request access to your personal data and receive a copy of the personal data we hold about you.'
          },
          {
            title: 'Right to Rectification',
            description: 'You can request the correction of inaccurate personal data or complete incomplete data.'
          },
          {
            title: 'Right to Erasure',
            description: 'You have the right to request the deletion of your personal data under certain circumstances.'
          },
          {
            title: 'Right to Restrict Processing',
            description: 'You can request the restriction of processing your personal data under specific conditions.'
          },
          {
            title: 'Right to Data Portability',
            description: 'You have the right to receive your personal data in a structured, commonly used, and machine-readable format.'
          },
          {
            title: 'Right to Object',
            description: 'You can object to the processing of your personal data under certain circumstances.'
          }
        ]
      },
      dataProcessing: {
        title: 'Data Processing',
        content: 'We process your personal data for the following purposes:',
        purposes: [
          'To provide and maintain our services',
          'To improve and personalize your experience',
          'To communicate with you about our services',
          'To comply with legal obligations',
          'To protect our rights and prevent fraud'
        ]
      },
      dataRetention: {
        title: 'Data Retention',
        content: 'We retain your personal data only for as long as necessary to fulfill the purposes for which it was collected, including legal, accounting, or reporting requirements. When we no longer need your personal data, we will securely delete or anonymize it.'
      },
      contact: {
        title: 'Contact Us',
        content: 'If you have any questions about our GDPR compliance or wish to exercise your rights, please contact our Data Protection Officer:',
        email: 'Email: privacy@sanctionscreener.eu'
      }
    },

    // About Page (new section)
    aboutPage: {
      heroTitle: 'About Sanction Screener',
      heroSubtitle: "We're on a mission to simplify compliance for businesses.",
      ourStory: 'Our Story',
      ourJourney: 'Our Journey',
      storyParagraph1: 'Sanction Screener was created by technology innovators who recognized a critical gap in the market: while sanctions compliance was becoming increasingly important, the tools available to businesses were often outdated, expensive, and difficult to use.',
      storyParagraph2: 'We set out to build a solution that would make compliance accessible, efficient, and reliable for businesses of all sizes. Our founders brought together expertise from financial compliance, artificial intelligence, and user experience design to create a modern platform for the digital age.',
      storyParagraph3: "Since our launch, we've worked hard to improve our product to serve businesses, from small fintech startups to established financial institutions. We're proud to have played a part in helping our clients maintain compliance and manage risk while focusing on growth and innovation.",
      ourMission: 'Our Mission',
      whyWeExist: "Why We Exist",
      missionParagraph1: 'Our mission is to simplify the process of compliance through technology. We believe that every business should have access to reliable, efficient, and affordable compliance tools that help them meet regulatory requirements and protect against risk.',
      missionParagraph2: "We're committed to leveling the playing field by providing advanced compliance technology that was once available only to the largest institutions. By doing so, we help create a safer and more transparent financial ecosystem.",
      ourVision: 'Our Vision',
      whereWereHeaded: "Where We're Headed",
      visionParagraph1: 'We envision a future where compliance is seamlessly integrated into every business process, where risk management becomes a competitive advantage rather than a burden, and where financial crime is more effectively prevented through collaborative and intelligent systems.',
      visionParagraph2: "As regulations continue to evolve and challenges become more complex, we're committed to staying at the forefront of compliance technology, continuously innovating to address emerging needs and risks.",
      ourValues: 'Our Values',
      whatDrivesUs: 'What Drives Us',
      valuesIntro: 'Our core values shape everything we do, from product development to customer service.',
      integrity: 'Integrity',
      integrityDesc: 'We operate with complete transparency and honesty in all our dealings, both with our customers and within our team.',
      innovation: 'Innovation',
      innovationDesc: "We continuously push the boundaries of what's possible in compliance technology, leveraging the latest advances in AI and data science.",
      customerFocus: 'Customer Focus',
      customerFocusDesc: "We listen carefully to our customers' needs and challenges, making their success our top priority in everything we build.",
      excellence: 'Excellence',
      excellenceDesc: "We're committed to delivering exceptional quality in our product, our service, and every customer interaction.",
      accessibility: 'Accessibility',
      accessibilityDesc: 'We believe compliance tools should be accessible to businesses of all sizes, not just the largest institutions with the biggest budgets.',
      adaptability: 'Adaptability',
      adaptabilityDesc: 'We stay agile and responsive to changing regulations, emerging risks, and evolving customer needs in a dynamic compliance landscape.'
    },

    // Signup Page
    signupPage: {
      heroTitle: 'Get Started with Sanction Screener',
      heroSubtitle: 'Sign up for your 14-day free trial. No credit card required.',
      createAccount: 'Create Your Account',
      firstName: 'First Name',
      lastName: 'Last Name',
      workEmail: 'Work Email',
      companyName: 'Company Name',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      phoneNumber: 'Phone Number',
      jobTitle: 'Job Title',
      companySize: 'Company Size',
      selectOption: 'Select an option',
      employees1to10: '1-10 employees',
      employees11to50: '11-50 employees',
      employees51to200: '51-200 employees',
      employees201to500: '201-500 employees',
      employees501to1000: '501-1000 employees',
      employees1000plus: '1000+ employees',
      selectTrialPlan: 'Select Your Trial Plan',
      perMonth: 'per month',
      recommended: 'Recommended',
      agreeToTerms: 'I agree to the',
      termsOfService: 'Terms of Service',
      and: 'and',
      privacyPolicy: 'Privacy Policy',
      createAccountAndStartTrial: 'Create Account & Start Free Trial',
      alreadyHaveAccount: 'Already have an account?',
      logIn: 'Log in',
      trialIncludes: 'Your 14-Day Free Trial Includes:',
      fullAccess: 'Full access to all features',
      unlimitedScreenings: 'Unlimited sanctions screening',
      completeWatchlists: 'Complete global watchlists',
      apiIntegration: 'API integration',
      expertSupport: 'Expert technical support',
      comprehensiveDocs: 'Comprehensive documentation',
      noCommitment: 'No Commitment',
      noCommitmentDesc: 'No credit card required to start your trial. Cancel anytime with no obligation.',
      needHelp: 'Need Help?',
      needHelpDesc: 'Contact our sales team for assistance or to learn more about custom enterprise solutions.',
      salesEmail: 'sales@sanctionscreener.eu',
      thankYou: 'Thank You for Signing Up!',
      confirmationSent: "We've sent a confirmation email to",
      checkInbox: 'Please check your inbox and click the verification link to activate your account.',
      returnToHome: 'Return to Home',
      // Form validation messages
      firstNameRequired: 'First name is required',
      lastNameRequired: 'Last name is required',
      emailRequired: 'Email is required',
      emailInvalid: 'Email is invalid',
      companyRequired: 'Company name is required',
      passwordRequired: 'Password is required',
      passwordMinLength: 'Password must be at least 8 characters',
      passwordsDoNotMatch: 'Passwords do not match',
      termsRequired: 'You must accept the terms and conditions',
      mustBeAtLeast8Chars: 'Must be at least 8 characters'
    },

    // Contact Page Section
    contactPage: {
      heroTitle: 'Contact Us',
      heroSubtitle: 'Have questions or need assistance? Our team is here to help you.',
      thankYou: 'Thank You!',
      successMessage: 'Your message has been sent successfully. We\'ll get back to you as soon as possible.',
      sendAnother: 'Send Another Message',
      sendUsMessage: 'Send Us a Message',
      nameLabel: 'Your Name *',
      emailLabel: 'Your Email *',
      companyLabel: 'Company',
      phoneLabel: 'Phone Number',
      subjectLabel: 'Subject *',
      subjectGeneral: 'General Inquiry',
      subjectSales: 'Sales Question',
      subjectTechnical: 'Technical Support',
      subjectPartnership: 'Partnership Opportunity',
      subjectMedia: 'Media Inquiry',
      messageLabel: 'Your Message *',
      sendButton: 'Send Message',
      contactInfoTitle: 'Contact Information',
      ourOfficesTitle: 'Our Offices',
      officeZagreb: 'Zagreb',
      officeZagrebAddress: 'Radnička cesta 50',
      officeZagrebCity: '10000 Zagreb, Croatia',
      officeLondon: 'London',
      officeLondonAddress: 'Lister House Lister Hill',
      officeLondonCity: 'Horsforth, Leeds, England, LS18 5AZ',
      phoneLabelShort: 'Phone:',
      emailLabelShort: 'Email:',
      supportChannelsTitle: 'Support Channels',
      emailSupportTitle: 'Email Support',
      generalInquiries: 'For general inquiries:',
      technicalSupport: 'For technical support:',
      phoneSupportTitle: 'Phone Support',
      customerServiceHours: 'Customer service hours:',
      serviceHours: 'Monday - Friday: 9:00 AM - 6:00 PM CET',
      international: 'International:',
      liveChatSupportTitle: 'Live Chat Support',
      liveChatSupportDesc: 'Available for Professional and Enterprise customers through our customer portal.',
      liveChatSupportLink: 'Log in to access chat support →',
      followUsTitle: 'Follow Us',
      faqTitle: 'Frequently Asked Questions',
      faqSubtitle: 'Quick answers to common questions',
      faq: [
        {
          question: 'What are your support hours?',
          answer: 'Our customer support team is available Monday through Friday, from 9:00 AM to 6:00 PM Central European Time. Enterprise customers have access to extended support hours.'
        },
        {
          question: 'How quickly can I expect a response?',
          answer: 'For general inquiries, we strive to respond within 24 hours during business days. Technical support tickets are prioritized based on severity and your service level, with response times ranging from 1 hour to 24 hours.'
        },
        {
          question: 'Do you offer on-site training and implementation?',
          answer: 'Yes, we offer on-site training and implementation services for Enterprise customers throughout Europe. Please contact our sales team to discuss your specific requirements and arrange a customized solution.'
        },
        {
          question: 'How do I upgrade my subscription plan?',
          answer: 'You can upgrade your subscription plan at any time through your account dashboard. If you need assistance, please contact our customer support team, and they will guide you through the process.'
        }
      ]
    },

    // --- Added for illustration internationalization ---
    individualSearchResults: 'Search Results',
    foundMatches: 'Found {count} matches for "{name}"',
    veryHigh: 'Very High',
    low: 'Low',
    pep: 'PEP',
    sanctions: 'Sanctions',
    veryHighRisk: 'Very High Risk',
    alternativeNames: 'Alternative Names',
    knownAssociates: 'Known Associates',
    // --- Added for case management, monitoring, and bulk upload illustrations ---
    refresh: 'Refresh',
    totalCases: 'Total Cases',
    open: 'Open',
    inProgress: 'In Progress',
    completed: 'Completed',
    matches: 'Matches',
    highRisk: 'High Risk',
    of: 'of',
    matchesReviewed: 'Matches Reviewed',
    monitoring: 'Monitoring',
    automaticRescreeningFrequency: 'Automatic Re-screening Frequency:',
    every6Months: 'Every 6 months',
    everyMonth: 'Every month',
    everyWeek: 'Every week',
    everyDay: 'Every day',
    totalMonitored: 'Total Monitored',
    checked: 'Checked',
    run: 'Run',
    remove: 'Remove',
    bulkScreening: 'Bulk Screening',
    clearAndNew: 'Clear & New',
    backToJobs: 'Back to Jobs',
    currentJob: 'Current Job',
    status: 'Status',
    highConfidenceMatches: 'High Confidence Matches',
    verificationId: 'Verification ID',
    actions: 'Actions',
    reviewNeeded: 'Review Needed',
    view: 'View'
  },
  hr: {
    // Navigation
    home: 'Početna',
    features: 'Značajke',
    pricing: 'Cijene',
    about: 'O nama',
    contact: 'Kontakt',
    login: 'Prijava',
    signup: 'Registracija',
    language: 'Jezik',

    // Home Page
    heroTitle: 'Provjerite klijente, osigurajte usklađenost.',
    heroSubtitle: 'Provjerite pojedince i tvrtke na globalnim listama sankcija uz napredno AI pretraživanje. Transformirajte ručne procese usklađenosti u automatizirane tijekove rada s dokumentacijom spremnom za reviziju.',
    getStarted: 'Započni',
    learnMore: 'Saznaj Više',
    trustedBy: 'Povjerenje vodećih tvrtki diljem svijeta',
    globalWatchlists: 'Globalne Nadzorne Liste',
    superfastResults: 'Superbrzi Rezultati',
    auditReady: 'Spremno za Reviziju',
    gdprCompliant: 'Usklađeno s GDPR-om',
    
    // Challenges Section
    challengesTitle: 'Prestanite se boriti s ručnim provjerama usklađenosti',
    challengesSubtitle: 'Tradicionalne provjere usklađenosti su spore, podložne greškama i zahtjevaju mnogo resursa. Vašem poslovanju treba pametniji pristup.',
    beforeProtego: 'Prije Sanction Screenera',
    beforeProtegoPoints: [
      'Ručne provjere traju satima ili danima',
      'Visoke stope lažnih pozitivnih rezultata troše vrijeme',
      'Višestruki sustavi stvaraju fragmentaciju',
      'Nekonzistentna dokumentacija za revizije',
      'Rizik od ljudske pogreške u kritičnim odlukama',
      'Odgođeno uvođenje klijenata'
    ],
    afterProtego: 'Nakon Sanction Screenera',
    afterProtegoPoints: [
      'Automatizirane provjere u dijelovima sekunde',
      'Pametna AI smanjuje lažne pozitivne rezultate za 80%',
      'Jedna platforma za sve globalne liste nadzora',
      'Automatska dokumentacija spremna za reviziju',
      'Konzistentne, pouzdane automatizirane odluke',
      'Ubrzano uvođenje u skladu s propisima'
    ],
    
    // Pillars Section
    pillarsTitle: 'Tri Stupa Izvrsnosti Usklađenosti',
    pillarsSubtitle: 'Sanction Screener transformira usklađenost iz tereta u konkurentsku prednost',
    pillars: [
      {
        icon: '⚡',
        title: 'Učinkovitost Kroz Automatizaciju',
        description: 'Transformirajte dane ručnog rada u sekunde automatizirane obrade. Oslobodite svoj tim za usklađenost da se fokusira na strateške inicijative.'
      },
      {
        icon: '🌐',
        title: 'Sveobuhvatna Pokrivenost',
        description: 'Pristupite svim glavnim globalnim listama nadzora kroz jednu platformu. EU, UN, OFAC, PEP registri i više – sve na jednom mjestu.'
      },
      {
        icon: '📋',
        title: 'Dokumentacija Spremna za Reviziju',
        description: 'Generirajte provjerljiva izvješća s jedinstvenim ID-ovima verifikacije. Pokažite usklađenost regulatorima s povjerenjem.'
      }
    ],
    
    // Features
    featuresTitle: 'Moćne značajke za modernu usklađenost',
    featuresSubtitle: 'Sve što trebate za pojednostavljeno provjeravanje usklađenosti i upravljanje rizikom',
    
    // Features Showcase Section
    productSuite: 'The Sanctions Screener Suite',
    featuresShowcaseTitle: 'Najsveobuhvatnija platforma za provjeru sankcija u industriji',
    featuresShowcaseSubtitle: 'Transformirajte provjeru usklađenosti iz ručnog tereta u konkurentsku prednost s našom integriranom platformom koja pokriva svaki aspekt usklađenosti sa sankcijama.',
    individualSearch: 'Pretraživanje pojedinaca i tvrtki',
    caseManagement: 'Upravljanje slučajevima',
    caseManagementDesc: 'Efficiently manage and track potential matches through a structured workflow. Our case management system provides complete visibility into review processes with comprehensive audit trails for regulatory compliance.',
    automatedMonitoring: 'Automatizirano praćenje',
    massUpload: 'Masovno učitavanje',
    individualSearchTitle: 'Brzo pretraživanje pojedinaca i tvrtki',
    individualSearchDesc: 'Provjerite pojedince i tvrtke prema globalnim listama sankcija u sekundama. Naši inteligentni algoritmi pružaju točne rezultate s bodovanjem pouzdanosti, smanjujući lažne pozitivne rezultate dok osiguravaju da nijedan stvarni rizik ne bude propušten.',
    individualSearchPoints: [
      'Provjera u dijelovima sekunde prema svim glavnim globalnim listama nadzora',
      'Pametno fuzzy podudaranje prepoznaje varijacije imena i aliasa',
      'Detaljno bodovanje pouzdanosti (0-100%) za prioritetni pregled',
      'Podrška za više jezika i nelatinične skripte',
      'Trenutačna izvješća spremna za reviziju s jedinstvenim ID-ovima verifikacije',
    ],
    caseManagementTitle: 'Pojednostavljeno upravljanje slučajevima',
    caseManagementDesc: 'Učinkovito upravljajte i pratite potencijalne podudarnosti kroz strukturirani tijek rada. Naš sustav za upravljanje slučajevima pruža potpunu vidljivost u procese pregleda s opsežnim revizijskim tragovima za regulatornu usklađenost.',
    caseManagementPoints: [
      'Centralizirana nadzorna ploča za sve statuse i napredak slučajeva',
      'Strukturirani tijek rada s prilagodljivim fazama pregleda',
      'Potpuni revizijski tragovi s vremenskim oznakama i praćenjem korisnika',
      'Suradnički proces pregleda s dodjeljivanjem timova',
      'Automatizirane obavijesti i upravljanje rokovima',
    ],
    automatedMonitoringTitle: 'Kontinuirano automatizirano praćenje',
    automatedMonitoringDesc: 'Ostanite usklađeni s automatiziranim ponovnim provjeravanjem koje nadzire vaše entitete u odnosu na promjenjive liste sankcija. Postavite prilagođene frekvencije i primajte trenutna upozorenja kada se identificiraju novi rizici, osiguravajući kontinuiranu usklađenost bez ručne intervencije.',
    automatedMonitoringPoints: [
      'Prilagodljive frekvencije provjere (dnevno do godišnje)',
      'Automatska upozorenja prilikom ažuriranja lista za nadzor',
      'Trenutne obavijesti za novootkrivene rizike',
      'Mogućnosti upravljanja masovnim nadzorom',
      'Povijesno praćenje svih aktivnosti nadzora',
    ],
    massUploadTitle: 'Masovno učitavanje velikog volumena',
    massUploadDesc: 'Obradite stotine entiteta istovremeno s našim mogućnostima masovne provjere. Savršeno za uvođenje novih baza podataka, periodične preglede usklađenosti ili sveobuhvatnu verifikaciju lanca opskrbe s detaljnom obradom serija i upravljanjem rezultatima.',
    massUploadPoints: [
      'Učitajte do 250 entiteta po seriji putem CSV/Excela',
      'Paralelna obrada za maksimalnu brzinu i učinkovitost',
      'Detaljni rezultati serije s pojedinačnim izvješćima o podudaranju',
      'Nadzorna ploča za praćenje napretka i upravljanje zadacima',
      'Izvozivi rezultati za daljnju analizu i dokumentaciju',
    ],
    trySearchDemo: 'Isprobajte Demo Pretraživanja',
    sanctionsScreening: 'Provjera Sankcija',
    welcomeUser: 'Dobrodošli, Korisnik',
    searchPlaceholder: 'Unesite ime za provjeru prema listama sankcija...',
    people: '👤 Osobe',
    companies: '🏢 Tvrtke',
    search: 'Pretraži',
    advancedSearch: 'Napredno Pretraživanje',
    advancedSearchDesc: 'Inteligentni algoritmi podudaranja imena prepoznaju varijacije u pravopisu i strukturi.',
    confidenceScoringTitle: 'Bodovanje Pouzdanosti',
    confidenceScoringDesc: 'Rezultati rangirani s detaljnim razinama pouzdanosti za identifikaciju relevantnih podudaranja.',
    complianceReady: 'Spremno za Usklađenost',
    complianceReadyDesc: 'Generirajte izvješća provjere s jedinstvenim ID-ovima za regulatornu usklađenost.',
    smartMatching: 'Pametni algoritam podudaranja',
    smartMatchingDesc: 'Napredni algoritmi fuzzy podudaranja prepoznaju varijacije imena, pravopisne pogreške i transliteracije na više jezika. Komponente strojnog učenja poboljšavaju točnost tijekom vremena, smanjujući lažne pozitivne rezultate do 80% s detaljnim bodovanjem pouzdanosti (0-100%).',
    riskClassification: 'Sustav klasifikacije rizika',
    riskClassificationDesc: 'Jasni vizualni pokazatelji s rizičnim razinama označenim bojama (Vrlo Visok, Visok, Srednji, Nizak). Prilagodivi pragovi rizika odgovaraju apetitu vaše organizacije, dok pametno prioritetiranje ističe podudaranja iznad 95% pouzdanosti za trenutačnu pozornost.',
    continuousMonitoring: 'Kontinuirano praćenje',
    continuousMonitoringDesc: 'Automatizirano ponovno provjeravanje kada se liste nadzora ažuriraju, s konfigurabilnim razdobljima (dnevno, tjedno, mjesečno, godišnje). Trenutačna upozorenja za novootkrivene rizike i detekcija promjena ističu specifične modifikacije u statusu rizika.',
    massScreening: 'Bulk Screening Capabilities',
    massScreeningDesc: 'Bulk upload and process thousands of entities simultaneously with CSV/Excel compatibility. Structured batch processing with detailed results and screening job management for both small and large datasets.',
    apiIntegration: 'Besprijekorna API integracija',
    apiIntegrationDesc: 'RESTful API s opsežnom dokumentacijom, unaprijed izgrađeni konektori za popularne CRM i ERP sustave. Podrška za webhook u arhitekturama vođenim događajima i prilagodljivo mapiranje podataka za složene integracije.',

    // How It Works Section
    howItWorksTitle: 'Kako Sanction Screener radi',
    howItWorksSubtitle: 'Jednostavan, ali moćan tijek rada koji osigurava usklađenost vašeg poslovanja',
    connect: 'Povezivanje',
    connectDesc: 'Vaše rješenje za usklađenost s sigurnošću na razini poduzeća i korisnički prijateljskim web sučeljem. Nije potrebno tehničko znanje.',
    screen: 'Provjeravanje',
    screenDesc: 'Trenutačno provjeravanje pojedinaca i tvrtki prema glavnim globalnim listama nadzora uključujući EU, UN i OFAC s odgovorom u sekundama.',
    review: 'Pregled',
    reviewDesc: 'Ispitivanje potencijalnih podudaranja kroz inteligentni sustav upravljanja slučajevima koji ističe ključne čimbenike rizika i smanjuje lažne pozitivne rezultate.',
    decide: 'Odluka',
    decideDesc: 'Odobravanje, odbijanje ili eskalacija slučajeva s povjerenjem, održavanje sveobuhvatnih tragova revizije koji zadovoljavaju regulatorne zahtjeve.',
    beyondBasic: 'Više od osnovnog provjeravanja',
    beyondBasicDesc: 'Naša platforma ne staje samo na provjeravanju. Pružamo kontinuirano praćenje, automatizirano ponovno provjeravanje kada se liste sankcija promijene, i detaljnu analitiku koja vam pomaže optimizirati procese usklađenosti. S našim poslovnim planom, također ćete primati regulatorna ažuriranja i savjetodavne usluge za usklađenost kako biste bili ispred promjenjivih zahtjeva.',

    // Pricing
    pricingTitle: 'Cjenovni planovi',
    pricingSubtitle: 'Odaberite pravi plan za vaše potrebe usklađenosti. Nadogradite u bilo kojem trenutku kako vaša organizacija raste.',
    starter: 'Početni',
    business: 'Poslovni',
    enterprise: 'Poduzeće',
    mostPopular: 'Najpopularniji',
    custom: 'Po mjeri',
    perMonth: '/mjesečno',
    unlimitedScreenings: 'Neograničeno provjeravanja',
    unlimitedTeam: 'Neograničeni članovi tima',
    premiumApi: 'Premium API pristup',
    oneYearHistory: '1-godišnja povijest pretraživanja',
    dedicatedManager: 'Namjenski upravitelj računa',
    upTo1000Screenings: 'Do 100 provjeravanja/mjesečno',
    upTo10000Screenings: 'Do 1000 provjeravanja/mjesečno',
    threeTeamMembers: '3 člana tima',
    tenTeamMembers: '10 članova tima',
    basicApiAccess: 'Osnovni API pristup',
    fullApiAccess: 'Potpuni API pristup',
    sevenDayHistory: '7-dnevna povijest pretraživanja',
    thirtyDayHistory: '30-dnevna povijest pretraživanja',
    batchScreening: 'Skupna provjera',

    // Pricing Page
    pricingPage: {
      heroTitle: 'Transparentne Cijene',
      heroSubtitle: 'Odaberite savršen plan za vaše potrebe usklađenosti bez skrivenih troškova.',
      annualBilling: 'Godišnje',
      monthlyBilling: 'Mjesečno',
      save20: 'Uštedite 20%',
      youSavePerYear: 'Uštedite {amount} godišnje',
      starterPlan: {
        title: 'Početni',
        subtitle: 'Savršen za mala poduzeća i startupove',
        priceMonthly: '€99',
        priceAnnual: '€79',
        billedAnnually: 'Naplaćuje se godišnje (€948/godišnje)',
        features: [
          'Do 100 pretraga mjesečno',
          '3 člana tima',
          'Aktivno praćenje',
          'Upravljanje slučajevima'
        ]
      },
      professionalPlan: {
        title: 'Professional',
        subtitle: 'Idealno za rastuća poduzeća s potrebama usklađenosti',
        priceMonthly: '€199',
        priceAnnual: '€159',
        billedAnnually: 'Naplaćuje se godišnje (€1.908/godišnje)',
        features: [
          'Do 1000 pretraga mjesečno',
          '10 članova tima',
          'Skupna provjera',
          'REST API pristup',
          'Aktivno praćenje',
          'Upravljanje slučajevima'
        ]
      },
      enterprisePlan: {
        title: 'Poduzeće',
        subtitle: 'Za velike organizacije sa složenim zahtjevima usklađenosti',
        price: 'Po mjeri',
        tailoredToNeeds: 'Prilagođeno vašim potrebama',
        features: [
          'Neograničene pretrage',
          'Namjenski upravitelj računa',
          'Prilagođene integracije',
          'Premium podrška',
          'SLA garancija',
          'Napredna sigurnost i usklađenost'
        ]
      },
      compareFeatures: {
        title: 'Usporedite Značajke',
        subtitle: 'Pronađite plan koji odgovara vašim potrebama usklađenosti',
        features: 'Značajke',
        monthlyScreenings: 'Mjesečna provjeravanja',
        euSanctionsLists: 'EU liste sankcija',
        unSanctionsLists: 'UN liste sankcija',
        ofacSanctionsLists: 'OFAC liste sankcija',
        nationalSanctionsLists: 'Nacionalne liste sankcija',
        pepsScreening: 'PEP provjeravanje',
        adverseMediaScreening: 'Provjeravanje negativnih medija',
        fuzzyMatchingAlgorithm: 'Fuzzy algoritam podudaranja',
        apiAccess: 'API pristup',
        webhookNotifications: 'Webhook obavijesti',
        auditLogs: 'Revizijski zapisi',
        customRiskRules: 'Prilagođena pravila rizika',
        onPremiseDeployment: 'On-premise implementacija',
        support: 'Podrška',
        limited: 'Ograničeno',
        basic: 'Osnovno',
        advanced: 'Napredno',
        premium: 'Premium',
        email: 'Email',
        emailAndChat: 'Email i Chat',
        priority247: '24/7 Prioritet',
        sevenDays: '7 dana',
        thirtyDays: '30 dana',
        oneYear: '1 godina',
        aiPoweredMatching: 'AI podudaranje',
        bulkUpload: 'Masovno učitavanje',
        caseManagement: 'Upravljanje slučajevima',
        activeMonitoring: 'Aktivno praćenje',
        // Add new keys for the actual values to be used in the table
        starterMonthlyScreenings: '100',
        professionalMonthlyScreenings: '1000',
        enterpriseMonthlyScreenings: 'Neograničeno provjeravanja'
      },
      faq: {
        title: 'Često Postavljana Pitanja',
        subtitle: 'Imate pitanja? Tu smo da pomognemo.',
        stillHaveQuestions: 'Još uvijek imate pitanja?',
        trialEnd: {
          question: 'Što se događa nakon moje besplatne probe?',
          answer: 'Nakon što vaša 14-dnevna besplatna proba završi, vaš račun će se automatski prebaciti na plan koji ste odabrali tijekom registracije. Poslat ćemo vam podsjetnike prije završetka probe, a možete otkazati ili promijeniti plan u bilo kojem trenutku bez kazni.'
        },
        paymentMethods: {
          question: 'Koje metode plaćanja prihvaćate?',
          answer: 'Prihvaćamo sve glavne kreditne kartice, uključujući Visa, Mastercard i American Express. Za Enterprise planove također nudimo plaćanje računom s neto-30 uvjetima.'
        },
        changePlans: {
          question: 'Mogu li kasnije promijeniti planove?',
          answer: 'Da, možete nadograditi ili smanjiti svoj plan u bilo kojem trenutku. Nadogradnje stupaju na snagu odmah, dok se smanjenja primjenjuju na kraju vašeg trenutnog ciklusa naplaćivanja.'
        },
        exceedLimit: {
          question: 'Što se događa ako premašim svoju mjesečnu granicu provjeravanja?',
          answer: 'Ako se približite granici provjeravanja, obavijestit ćemo vas kako biste mogli nadograditi ako je potrebno. Ako premašite granicu, dodatna provjeravanja će se naplatiti po stopi po provjeravanju na temelju vašeg plana.'
        },
        nonProfit: {
          question: 'Nudite li popuste za neprofitne organizacije?',
          answer: 'Da, nudimo posebne cijene za kvalificirane neprofitne organizacije. Molimo kontaktirajte naš prodajni tim da saznate više o našem programu za neprofitne organizacije.'
        },
        contractPeriod: {
          question: 'Postoji li minimalno razdoblje ugovora?',
          answer: 'Ne, naši Početni i Poslovni planovi su mjesečni bez minimalnog razdoblja ugovora kada se naplaćuju mjesečno. Godišnji planovi se plaćaju unaprijed za 12 mjeseci. Enterprise planovi mogu imati prilagođene uvjete.'
        }
      },
      cta: {
        title: 'Spremni za Jačanje Vaše Usklađenosti?',
        subtitle: 'Pridružite se tisućama poduzeća diljem Europe koji vjeruju Sanction Screeneru.',
        startFreeTrial: 'Isprobajte besplatno',
        requestDemo: 'Zatražite demo'
      }
    },

    // CTA Section
    startFreeTrial: 'Isprobajte besplatno',
    contactSales: 'Kontaktirajte prodaju',
    
    // Newsletter Section
    newsletterTitle: 'Ostanite u toku',
    newsletterSubtitle: 'Pretplatite se na naš newsletter za najnovije obavijesti, nove značajke i ekskluzivne ponude.',
    newsletterPlaceholder: 'Unesite vašu e-mail adresu',
    newsletterButton: 'Pretplatite se',
    newsletterSuccess: 'Hvala na pretplati!',
    newsletterError: 'Došlo je do greške. Molimo pokušajte ponovo kasnije.',
    
    // About
    aboutTitle: 'O Sanction Screeneru',
    aboutSubtitle: 'Vaš pouzdan partner u kibernetičkoj sigurnosti',
    ourMission: 'Naša Misija',
    ourMissionDesc: 'Pružiti najsuvremenija rješenja za kibernetičku sigurnost koja štite poduzeća u digitalnom dobu',
    ourTeam: 'Naš Tim',
    ourTeamDesc: 'Iskusni profesionalci posvećeni vašoj sigurnosti',
    
    // Contact
    contactTitle: 'Kontaktirajte Nas',
    contactSubtitle: 'Tu smo da pomognemo',
    name: 'Ime',
    email: 'Email',
    message: 'Poruka',
    send: 'Pošalji Poruku',
    contactInfo: 'Kontakt Informacije',
    address: 'Adresa',
    phone: 'Telefon',
    
    // Auth
    emailAddress: 'Email Adresa',
    password: 'Lozinka',
    confirmPassword: 'Potvrdi Lozinku',
    forgotPassword: 'Zaboravili ste lozinku?',
    rememberMe: 'Zapamti Me',
    createAccount: 'Kreiraj Račun',
    alreadyHaveAccount: 'Već imate račun?',
    dontHaveAccount: 'Nemate račun?',
    
    // Footer
    companyName: 'Sanction Screener',
    companyDescription: 'Napredno rješenje za provjeru sankcija za financijske institucije, fintech tvrtke i regulirana poduzeća diljem Europe.',
    quickLinks: 'Brze poveznice',
    aboutUs: 'O nama',
    contactUs: 'Kontaktirajte nas',
    emailLabel: 'Email:',
    privacyPolicy: 'Pravila privatnosti',
    termsOfService: 'Uvjeti korištenja',
    gdprCompliance: 'Usklađenost s GDPR-om',
    allRightsReserved: 'Sva prava pridržana',
    euCompliance: 'Usklađeno s EU regulativama, uključujući 5AMLD i 6AMLD. Registrirano u Europskoj Uniji.',
    
    // Common
    loading: 'Učitavanje...',
    error: 'Greška',
    success: 'Uspjeh',
    save: 'Spremi',
    cancel: 'Odustani',
    delete: 'Izbriši',
    edit: 'Uredi',
    view: 'Pregledaj',
    filter: 'Filtriraj',
    sort: 'Sortiraj',
    more: 'Više',
    less: 'Manje',

    // Cookie Consent
    cookieConsent: {
      title: 'Postavke kolačića',
      description: 'Koristimo kolačiće za poboljšanje vašeg iskustva, analizu prometa na stranici i pružanje personaliziranog sadržaja. Možete prilagoditi svoje postavke u nastavku.',
      acceptAll: 'Prihvati sve',
      rejectAll: 'Odbij sve',
      customize: 'Prilagodi',
      settingsTitle: 'Postavke kolačića',
      saveSettings: 'Spremi postavke',
      cancel: 'Odustani',
      essential: {
        title: 'Osnovni kolačići',
        description: 'Osnovni kolačići su potrebni za osnovnu funkcionalnost stranice.'
      },
      analytics: {
        title: 'Analitički kolačići',
        description: 'Pomažu nam razumjeti kako posjetitelji koriste našu web stranicu.'
      },
      marketing: {
        title: 'Marketinški kolačići',
        description: 'Omogućavaju nam prikazivanje relevantnih oglasa i sadržaja.'
      }
    },

    // Privacy Policy
    lastUpdated: 'Zadnje ažurirano',
    privacy: {
      introduction: {
        title: '1. Uvod',
        content: 'Sanction Screener ("mi," "naš," ili "nas") posvećen je zaštiti vaše privatnosti. Ova Pravila privatnosti objašnjavaju kako se vaši osobni podaci prikupljaju, koriste i objavljuju od strane Sanction Screenera kada koristite našu web stranicu, usluge i aplikacije (zajednički, "Usluge").'
      },
      informationCollection: {
        title: '2. Informacije koje prikupljamo',
        subtitle: 'Prikupljamo informacije koje nam izravno dostavite, kao što su:',
        directInfo: [
          'Informacije o računu: Kada se registrirate za račun, prikupljamo vaše ime, email adresu, lozinku, naziv tvrtke, radno mjesto i kontakt podatke.',
          'Podaci o provjeri sankcija: Informacije koje dostavljate za provjeru sankcija, koje mogu uključivati imena pojedinaca ili tvrtki, identifikatore i povezane informacije.',
          'Informacije o plaćanju: Kada se pretplatite na naše plaćene usluge, prikupljamo informacije o plaćanju, iako ne pohranjujemo potpune podatke o platnoj kartici.',
          'Komunikacije: Kada nas izravno kontaktirate, bilježimo komunikaciju i sve dodatne informacije koje dostavite.'
        ],
        automaticSubtitle: 'Također automatski prikupljamo određene informacije kada koristite naše Usluge:',
        automaticInfo: [
          'Podaci o korištenju: Informacije o tome kako koristite naše Usluge, uključujući podatke o zapisima, informacije o uređaju i podatke analitike.',
          'Kolačići i slične tehnologije: Koristimo kolačiće i slične tehnologije za prikupljanje informacija o vašim aktivnostima pregledavanja.'
        ]
      },
      informationUse: {
        title: '3. Kako koristimo vaše informacije',
        subtitle: 'Koristimo prikupljene informacije za:',
        uses: [
          'To provide, maintain, and improve our Services.',
          'To process your transactions and manage your subscriptions.',
          'To respond to your comments, questions, and requests.',
          'To send you technical notices, updates, security alerts, and support messages.',
          'To communicate with you about products, services, offers, and events offered by Sanction Screener.'
        ]
      },
      dataRetention: {
        title: '4. Zadržavanje podataka',
        content: 'Zadržavamo osobne podatke onoliko dugo koliko je potrebno za ispunjenje svrha navedenih u ovim Pravilima privatnosti, osim ako zakon ne zahtijeva ili ne dopušta duže razdoblje zadržavanja. Za podatke o provjeri sankcija, vodimo evidenciju u skladu s primjenjivim propisima o sprječavanju pranja novca i zahtjevima usklađenosti.'
      },
      dataSharing: {
        title: '5. Dijeljenje i objavljivanje podataka',
        subtitle: 'Možemo podijeliti vaše informacije u sljedećim situacijama:',
        situations: [
          'S pružateljima usluga i poslovnim partnerima koji nam pomažu u pružanju naših Usluga;',
          'U vezi s ili tijekom pregovora o bilo kojem spajanju, prodaji imovine tvrtke, financiranju ili preuzimanju cijelog ili dijela našeg poslovanja;',
          'Ako smatramo da je objavljivanje potrebno za usklađenost s primjenjivim zakonima, propisima, pravnim postupcima ili zahtjevima vlade;',
          'Za provedbu naših ugovora, pravila i uvjeta korištenja;',
          'Za zaštitu sigurnosti ili integriteta naših Usluga;',
          'S vašim pristankom ili prema vašim uputama.'
        ]
      },
      gdpr: {
        title: '6. Usklađenost s GDPR-om',
        content: 'Za korisnike u Europskom gospodarskom prostoru (EEA), obrađujemo osobne podatke u skladu s Općom uredbom o zaštiti podataka (GDPR). Djelujemo i kao kontrolor i kao izvršitelj obrade podataka, ovisno o kontekstu.',
        rightsSubtitle: 'Prema GDPR-u, imate sljedeća prava:',
        rights: [
          'Pravo na pristup vašim osobnim podacima;',
          'Pravo na ispravak netočnih osobnih podataka;',
          'Pravo na brisanje vaših osobnih podataka;',
          'Pravo na ograničenje obrade vaših osobnih podataka;',
          'Pravo na prenosivost podataka;',
          'Pravo na prigovor obradi vaših osobnih podataka;',
          'Pravo na podnošenje pritužbe nadzornom tijelu.'
        ]
      },
      dataSecurity: {
        title: '7. Sigurnost podataka',
        content: 'Dajemo prioritet sigurnosti vaših osobnih podataka i implementirali smo sveobuhvatne tehničke, administrativne i fizičke mjere zaštite dizajnirane za zaštitu vaših podataka. Naše sigurnosne mjere uključuju robusne protokole šifriranja, sigurnu konfiguraciju baze podataka, redovite sigurnosne revizije i stroge kontrole pristupa. Iako smo posvećeni održavanju visokih sigurnosnih standarda i kontinuiranom poboljšanju naših zaštita, važno je napomenuti da nijedan sustav ne može pružiti 100% garanciju protiv svih mogućih sigurnosnih prijetnji u današnjem digitalnom okruženju.'
      },
      internationalTransfers: {
        title: '8. Međunarodni prijenosi podataka',
        content: 'Vaše informacije mogu biti prenesene i obrađene u zemljama koje nisu zemlja u kojoj boravite. Ove zemlje mogu imati zakone o zaštiti podataka koji se razlikuju od onih u vašoj zemlji. Osiguravamo da su na mjestu odgovarajuće zaštitne mjere za zaštitu vaših osobnih podataka prilikom međunarodnog prijenosa.'
      },
      changes: {
        title: '9. Promjene ovih Pravila privatnosti',
        content: 'Možemo povremeno mijenjati ova Pravila privatnosti. Ako napravimo značajne promjene, obavijestit ćemo vas putem emaila ili kroz Usluge prije nego što promjene postanu učinkovite.'
      },
      contact: {
        title: '10. Kontaktirajte nas',
        content: 'Ako imate bilo kakva pitanja ili nedoumice o ovim Pravilima privatnosti ili našim praksama obrade podataka, molimo kontaktirajte nas na:',
        email: 'Email'
      }
    },

    // Terms of Service
    termsOfService: {
      title: 'Uvjeti korištenja',
      acceptance: {
        title: 'Prihvaćanje uvjeta',
        content: 'Pristupom i korištenjem Sanction Screener usluga, pristajete biti vezani ovim Uvjetima korištenja. Ako se ne slažete s ovim uvjetima, molimo vas da ne koristite naše usluge.'
      },
      services: {
        title: 'Opis usluga',
        content: 'Sanction Screener pruža usluge provjere sankcija i verifikacije usklađenosti. Zadržavamo pravo modificirati, obustaviti ili prekinuti bilo koji aspekt naših usluga u bilo kojem trenutku.'
      },
      userObligations: {
        title: 'Obveze korisnika',
        items: [
          'Pružanje točnih i potpunih informacija prilikom korištenja naših usluga',
          'Održavanje povjerljivosti podataka za pristup vašem računu',
          'Korištenje naših usluga u skladu sa svim primjenjivim zakonima i propisima',
          'Ne pokušavati reverse engineering ili ometati naše usluge',
          'Ne koristiti naše usluge za bilo kakve ilegalne ili neovlaštene svrhe'
        ]
      },
      privacy: {
        title: 'Privatnost i zaštita podataka',
        content: 'Posvećeni smo zaštiti vaše privatnosti i rukovanju vašim podacima u skladu s primjenjivim zakonima o zaštiti podataka. Naša Politika privatnosti objašnjava kako prikupljamo, koristimo i štitimo vaše podatke.'
      },
      liability: {
        title: 'Ograničenje odgovornosti',
        content: 'Sanction Screener ne snosi odgovornost za bilo kakve neizravne, slučajne, posebne, posljedične ili kaznene štete koje proizlaze iz vašeg korištenja ili nemogućnosti korištenja naših usluga.'
      },
      termination: {
        title: 'Raskid',
        content: 'Zadržavamo pravo prekida ili obustave vašeg pristupa našim uslugama u bilo kojem trenutku, bez prethodne obavijesti, iz bilo kojeg razloga, uključujući ako vjerujemo da ste prekršili ove Uvjete korištenja.'
      },
      changes: {
        title: 'Promjene uvjeta',
        content: 'Možemo modificirati ove Uvjete korištenja u bilo kojem trenutku. Obavijestit ćemo vas o svim značajnim promjenama objavljivanjem novih Uvjeta korištenja na našoj web stranici.'
      },
      contact: {
        title: 'Kontakt informacije',
        content: 'Ako imate bilo kakvih pitanja o ovim Uvjetima korištenja, molimo kontaktirajte nas na support@sanctionscreener.eu.'
      }
    },

    // GDPR Page
    gdpr: {
      title: 'Usklađenost s GDPR-om',
      introduction: {
        title: 'Uvod',
        content: 'U Sanction Screeneru smo predani zaštiti vaše privatnosti i osiguravanju sigurnosti vaših osobnih podataka. Ova stranica o usklađenosti s GDPR-om opisuje kako prikupljamo, obrađujemo i štitimo vaše osobne podatke u skladu s Općom uredbom o zaštiti podataka (GDPR) i drugim primjenjivim zakonima o zaštiti podataka.'
      },
      rights: {
        title: 'Vaša prava prema GDPR-u',
        list: [
          {
            title: 'Pravo na pristup',
            description: 'Imate pravo zatražiti pristup svojim osobnim podacima i dobiti kopiju osobnih podataka koje držimo o vama.'
          },
          {
            title: 'Pravo na ispravak',
            description: 'Možete zatražiti ispravak netočnih osobnih podataka ili dopunu nepotpunih podataka.'
          },
          {
            title: 'Pravo na brisanje',
            description: 'Imate pravo zatražiti brisanje svojih osobnih podataka pod određenim okolnostima.'
          },
          {
            title: 'Pravo na ograničenje obrade',
            description: 'Možete zatražiti ograničenje obrade vaših osobnih podataka pod specifičnim uvjetima.'
          },
          {
            title: 'Pravo na prenosivost podataka',
            description: 'Imate pravo primiti svoje osobne podatke u strukturiranom, uobičajenom i strojno čitljivom formatu.'
          },
          {
            title: 'Pravo na prigovor',
            description: 'Možete se usprotiviti obradi vaših osobnih podataka pod određenim okolnostima.'
          }
        ]
      },
      dataProcessing: {
        title: 'Obrada podataka',
        content: 'Vaše osobne podatke obrađujemo u sljedeće svrhe:',
        purposes: [
          'Za pružanje i održavanje naših usluga',
          'Za poboljšanje i personalizaciju vašeg iskustva',
          'Za komunikaciju s vama o našim uslugama',
          'Za poštivanje zakonskih obveza',
          'Za zaštitu naših prava i sprječavanje prijevara'
        ]
      },
      dataRetention: {
        title: 'Zadržavanje podataka',
        content: 'Vaše osobne podatke zadržavamo samo onoliko dugo koliko je potrebno za ispunjenje svrha zbog kojih su prikupljeni, uključujući zakonske, računovodstvene ili izvještajne zahtjeve. Kada više ne budemo trebali vaše osobne podatke, sigurno ćemo ih izbrisati ili anonimizirati.'
      },
      contact: {
        title: 'Kontaktirajte nas',
        content: 'Ako imate pitanja o našoj usklađenosti s GDPR-om ili želite ostvariti svoja prava, molimo kontaktirajte našeg službenika za zaštitu podataka:',
        email: 'Email: privacy@sanctionscreener.eu'
      }
    },

    // About Page (new section)
    aboutPage: {
      heroTitle: 'O Sanction Screeneru',
      heroSubtitle: 'Naša misija je pojednostaviti proces usklađenosti za poduzeća.',
      ourStory: 'Naša priča',
      ourJourney: 'Naš put',
      storyParagraph1: 'Sanction Screener su stvorili tehnološki inovatori koji su prepoznali kritičnu prazninu na tržištu: dok je usklađenost sa sankcijama postajala sve važnija, alati dostupni poduzećima često su bili zastarjeli, skupi i teški za korištenje.',
      storyParagraph2: 'Odlučili smo izgraditi rješenje koje bi učinilo usklađenost dostupnom, učinkovitom i pouzdanom za poduzeća svih veličina. Naši osnivači spojili su stručnost iz financijske usklađenosti, umjetne inteligencije i dizajna korisničkog iskustva kako bi stvorili modernu platformu za digitalno doba.',
      storyParagraph3: 'Od našeg pokretanja, naporno radimo na poboljšanju našeg proizvoda kako bismo služili poduzećima, od malih fintech startupa do etabliranih financijskih institucija. Ponosni smo što smo pomogli našim klijentima održati usklađenost i upravljati rizikom, dok se oni fokusiraju na rast i inovacije.',
      ourMission: 'Naša misija',
      whyWeExist: 'Zašto postojimo',
      missionParagraph1: 'Naša misija je pojednostaviti proces usklađenosti kroz tehnologiju. Vjerujemo da svako poduzeće treba imati pristup pouzdanim, učinkovitim i pristupačnim alatima za usklađenost koji im pomažu ispuniti regulatorne zahtjeve i zaštititi se od rizika.',
      missionParagraph2: 'Posvećeni smo izjednačavanju uvjeta pružanjem napredne tehnologije za usklađenost koja je nekad bila dostupna samo najvećim institucijama. Time pomažemo stvoriti sigurniji i transparentniji financijski ekosustav.',
      ourVision: 'Naša vizija',
      whereWereHeaded: 'Kamo idemo',
      visionParagraph1: 'Zamišljamo budućnost u kojoj je usklađenost besprijekorno integrirana u svaki poslovni proces, gdje upravljanje rizikom postaje konkurentska prednost, a ne teret, i gdje se financijski kriminal učinkovitije sprječava kroz suradničke i inteligentne sustave.',
      visionParagraph2: 'Kako se regulative nastavljaju razvijati, a izazovi postaju složeniji, posvećeni smo ostati na čelu tehnologije usklađenosti, kontinuirano inovirajući kako bismo odgovorili na nove potrebe i rizike.',
      ourValues: 'Naše vrijednosti',
      whatDrivesUs: 'Što nas pokreće',
      valuesIntro: 'Naše temeljne vrijednosti oblikuju sve što radimo, od razvoja proizvoda do korisničke podrške.',
      integrity: 'Integritet',
      integrityDesc: 'Djelujemo s potpunom transparentnošću i poštenjem u svim našim odnosima, kako s klijentima tako i unutar tima.',
      innovation: 'Inovacija',
      innovationDesc: 'Neprestano pomičemo granice mogućeg u tehnologiji usklađenosti, koristeći najnovija dostignuća u AI-u i znanosti o podacima.',
      customerFocus: 'Fokus na klijenta',
      customerFocusDesc: 'Pažljivo slušamo potrebe i izazove naših klijenata, čineći njihov uspjeh našim glavnim prioritetom u svemu što gradimo.',
      excellence: 'Izvrsnost',
      excellenceDesc: 'Posvećeni smo pružanju iznimne kvalitete u našem proizvodu, usluzi i svakom kontaktu s korisnicima.',
      accessibility: 'Pristupačnost',
      accessibilityDesc: 'Vjerujemo da alati za usklađenost trebaju biti dostupni poduzećima svih veličina, a ne samo najvećima s najvećim budžetima.',
      adaptability: 'Prilagodljivost',
      adaptabilityDesc: 'Ostajemo agilni i odgovaramo na promjene regulative, nove rizike i potrebe korisnika u dinamičnom okruženju usklađenosti.'
    },

    // Signup Page
    signupPage: {
      heroTitle: 'Započnite sa Sanction Screenerom',
      heroSubtitle: 'Registrirajte se za 14-dnevnu besplatnu probu. Kreditna kartica nije potrebna.',
      createAccount: 'Kreirajte svoj račun',
      firstName: 'Ime',
      lastName: 'Prezime',
      workEmail: 'Poslovni email',
      companyName: 'Naziv tvrtke',
      password: 'Lozinka',
      confirmPassword: 'Potvrdi lozinku',
      phoneNumber: 'Broj telefona',
      jobTitle: 'Radno mjesto',
      companySize: 'Veličina tvrtke',
      selectOption: 'Odaberite opciju',
      employees1to10: '1-10 zaposlenika',
      employees11to50: '11-50 zaposlenika',
      employees51to200: '51-200 zaposlenika',
      employees201to500: '201-500 zaposlenika',
      employees501to1000: '501-1000 zaposlenika',
      employees1000plus: '1000+ zaposlenika',
      selectTrialPlan: 'Odaberite plan za probu',
      perMonth: 'mjesečno',
      recommended: 'Preporučeno',
      agreeToTerms: 'Slažem se s',
      termsOfService: 'Uvjetima korištenja',
      and: 'i',
      privacyPolicy: 'Pravilima privatnosti',
      createAccountAndStartTrial: 'Kreiraj račun i započni besplatnu probu',
      alreadyHaveAccount: 'Već imate račun?',
      logIn: 'Prijavite se',
      trialIncludes: 'Vaša 14-dnevna besplatna proba uključuje:',
      fullAccess: 'Potpuni pristup svim značajkama',
      unlimitedScreenings: 'Neograničeno provjeravanje sankcija',
      completeWatchlists: 'Potpune globalne liste nadzora',
      apiIntegration: 'API integracija',
      expertSupport: 'Stručna tehnička podrška',
      comprehensiveDocs: 'Opsežna dokumentacija',
      noCommitment: 'Bez obveze',
      noCommitmentDesc: 'Kreditna kartica nije potrebna za početak probe. Otkažite u bilo kojem trenutku bez obveze.',
      needHelp: 'Trebate pomoć?',
      needHelpDesc: 'Kontaktirajte naš prodajni tim za pomoć ili da saznate više o prilagođenim enterprise rješenjima.',
      salesEmail: 'sales@sanctionscreener.eu',
      thankYou: 'Hvala na registraciji!',
      confirmationSent: 'Poslali smo potvrdni email na',
      checkInbox: 'Molimo provjerite svoj inbox i kliknite na link za verifikaciju kako biste aktivirali svoj račun.',
      returnToHome: 'Povratak na početnu',
      // Form validation messages
      firstNameRequired: 'Ime je obavezno',
      lastNameRequired: 'Prezime je obavezno',
      emailRequired: 'Email je obavezan',
      emailInvalid: 'Email nije valjan',
      companyRequired: 'Naziv tvrtke je obavezan',
      passwordRequired: 'Lozinka je obavezna',
      passwordMinLength: 'Lozinka mora imati najmanje 8 znakova',
      passwordsDoNotMatch: 'Lozinke se ne podudaraju',
      termsRequired: 'Morate prihvatiti uvjete i odredbe',
      mustBeAtLeast8Chars: 'Mora imati najmanje 8 znakova'
    },

    // Contact Page Section
    contactPage: {
      heroTitle: 'Kontaktirajte nas',
      heroSubtitle: 'Imate pitanja ili trebate pomoć? Naš tim je ovdje za vas.',
      thankYou: 'Hvala!',
      successMessage: 'Vaša poruka je uspješno poslana. Odgovorit ćemo vam u najkraćem mogućem roku.',
      sendAnother: 'Pošaljite još jednu poruku',
      sendUsMessage: 'Pošaljite nam poruku',
      nameLabel: 'Vaše ime *',
      emailLabel: 'Vaš email *',
      companyLabel: 'Tvrtka',
      phoneLabel: 'Broj telefona',
      subjectLabel: 'Predmet *',
      subjectGeneral: 'Opći upit',
      subjectSales: 'Prodajni upit',
      subjectTechnical: 'Tehnička podrška',
      subjectPartnership: 'Prilika za partnerstvo',
      subjectMedia: 'Medijski upit',
      messageLabel: 'Vaša poruka *',
      sendButton: 'Pošalji poruku',
      contactInfoTitle: 'Kontakt informacije',
      ourOfficesTitle: 'Naši uredi',
      officeZagreb: 'Zagreb',
      officeZagrebAddress: 'Radnička cesta 50',
      officeZagrebCity: '10000 Zagreb, Hrvatska',
      officeLondon: 'London',
      officeLondonAddress: 'Lister House Lister Hill',
      officeLondonCity: 'Horsforth, Leeds, England, LS18 5AZ',
      phoneLabelShort: 'Telefon:',
      emailLabelShort: 'Email:',
      supportChannelsTitle: 'Kanali podrške',
      emailSupportTitle: 'Podrška putem emaila',
      generalInquiries: 'Za opće upite:',
      technicalSupport: 'Za tehničku podršku:',
      phoneSupportTitle: 'Podrška putem telefona',
      customerServiceHours: 'Radno vrijeme službe za korisnike:',
      serviceHours: 'Ponedjeljak - Petak: 9:00 - 18:00 CET',
      international: 'Međunarodno:',
      liveChatSupportTitle: 'Podrška putem chata',
      liveChatSupportDesc: 'Dostupno za profesionalne i enterprise korisnike putem našeg korisničkog portala.',
      liveChatSupportLink: 'Prijavite se za pristup chat podršci →',
      followUsTitle: 'Pratite nas',
      faqTitle: 'Često postavljana pitanja',
      faqSubtitle: 'Brzi odgovori na česta pitanja',
      faq: [
        {
          question: 'Koje je vaše radno vrijeme podrške?',
          answer: 'Naš tim za korisničku podršku dostupan je od ponedjeljka do petka, od 9:00 do 18:00 po srednjoeuropskom vremenu. Enterprise korisnici imaju pristup produženom radnom vremenu podrške.'
        },
        {
          question: 'Koliko brzo mogu očekivati odgovor?',
          answer: 'Za opće upite nastojimo odgovoriti u roku od 24 sata tijekom radnih dana. Tehnički zahtjevi se prioritetiziraju prema hitnosti i vašoj razini usluge, s vremenom odgovora od 1 do 24 sata.'
        },
        {
          question: 'Nudite li obuku i implementaciju na licu mjesta?',
          answer: 'Da, nudimo usluge obuke i implementacije na licu mjesta za enterprise korisnike diljem Europe. Kontaktirajte naš prodajni tim kako biste dogovorili rješenje prilagođeno vašim potrebama.'
        },
        {
          question: 'Kako mogu nadograditi svoj paket pretplate?',
          answer: 'Svoj paket pretplate možete nadograditi u bilo kojem trenutku putem korisničkog sučelja. Ako trebate pomoć, kontaktirajte našu korisničku podršku i vodit ćemo vas kroz proces.'
        }
      ]
    },

    // --- Added for illustration internationalization ---
    individualSearchResults: 'Rezultati pretrage',
    foundMatches: 'Pronađena {count} podudaranja za "{name}"',
    veryHigh: 'Vrlo visok',
    low: 'Nizak',
    pep: 'PEP',
    sanctions: 'Sankcije',
    veryHighRisk: 'Vrlo visok rizik',
    alternativeNames: 'Alternativna imena',
    knownAssociates: 'Poznati suradnici',
    // --- Added for case management, monitoring, and bulk upload illustrations ---
    refresh: 'Osvježi',
    totalCases: 'Ukupno slučajeva',
    open: 'Otvoreno',
    inProgress: 'U tijeku',
    completed: 'Završeno',
    matches: 'Podudaranja',
    highRisk: 'Visok rizik',
    of: 'od',
    matchesReviewed: 'Pregledanih podudaranja',
    monitoring: 'Praćenje',
    automaticRescreeningFrequency: 'Automatska frekvencija ponovne provjere:',
    every6Months: 'Svakih 6 mjeseci',
    everyMonth: 'Svakog mjeseca',
    everyWeek: 'Svakog tjedna',
    everyDay: 'Svakog dana',
    totalMonitored: 'Ukupno nadziranih',
    checked: 'Provjereno',
    run: 'Pokreni',
    remove: 'Ukloni',
    bulkScreening: 'Masovno provjeravanje',
    clearAndNew: 'Očisti i novo',
    backToJobs: 'Natrag na zadatke',
    currentJob: 'Trenutni zadatak',
    status: 'Status',
    highConfidenceMatches: 'Podudaranja visokog povjerenja',
    verificationId: 'ID provjere',
    actions: 'Radnje',
    reviewNeeded: 'Potrebna provjera',
    view: 'Pregledaj'
  }
}; 